import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys, type IntegrationOut } from "../shared";

export const GOOGLE_CALENDAR_KEY = "google_calendar";
const GOOGLE_AUTH_HOST = "accounts.google.com";

/** Typed view of the loosely-typed integration row (`config` is free-form JSON). */
export interface CalendarState {
  /** The platform has Google sign-in credentials; without them nobody can connect. */
  available: boolean;
  connected: boolean;
  calendarId: string | null;
  calendarName: string | null;
  accountEmail: string | null;
  createMeet: boolean;
}

export function calendarStateOf(integration: IntegrationOut | null | undefined): CalendarState {
  const config = integration?.config ?? {};
  const text = (key: string): string | null => {
    const value = config[key];
    return typeof value === "string" && value !== "" ? value : null;
  };
  return {
    available: integration?.available ?? false,
    connected: integration?.status === "connected",
    calendarId: text("calendar_id"),
    calendarName: text("calendar_name"),
    accountEmail: text("account_email"),
    createMeet: config.create_meet === true,
  };
}

/** Never navigate to anything but Google's https consent screen. */
export function isGoogleAuthorizeUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === GOOGLE_AUTH_HOST;
  } catch {
    return false;
  }
}

/** The workspace's Google Calendar row from `GET /v1/integrations` (shares the Integrations cache). */
export function useCalendarIntegration(enabled = true) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: wsKeys.integrations(wsId),
    queryFn: () => unwrap(api.GET("/v1/integrations")),
    enabled,
    select: (all) => all.find((integration) => integration.key === GOOGLE_CALENDAR_KEY) ?? null,
  });
}

/** `GET /v1/integrations/google_calendar/calendars`: only asked for once a calendar is connected. */
export function useCalendarChoices(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...wsKeys.integrations(wsId), GOOGLE_CALENDAR_KEY, "calendars"] as const,
    queryFn: () => unwrap(api.GET("/v1/integrations/google_calendar/calendars")),
    enabled,
    retry: false,
  });
}

export function useCalendarMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: wsKeys.integrations(wsId) });
  const connect = useMutation({ mutationFn: () => unwrap(api.POST("/v1/integrations/google_calendar/connect")) });
  const select = useMutation({
    mutationFn: (body: { calendar_id: string; create_meet: boolean }) => unwrap(api.PUT("/v1/integrations/google_calendar/calendar", { body })),
    onSuccess: refresh,
  });
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/integrations/{key}", { params: { path: { key: GOOGLE_CALENDAR_KEY } } })),
    onSuccess: refresh,
  });
  return { connect, select, disconnect };
}
